import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import {
	useAnalyticsData,
	useUserFunnelData,
	useSubscribersData,
	useUserByPlatformData,
} from '../../hooks/useAnalyticsApi';
import { 
	formatCurrency, 
	formatPercentage, 
	getChangeType, 
	transformDataForChart, 
	transformMultiSeriesDataForChart 
} from './utils';
import { originalStatsData, originalDataViewsConfig } from './constants';
import Stats from './Stats';
import DataView from './DataView';
import ExportButtons from './ExportButtons';

export default function StandardAnalytics({ dateRange, projectId }) {
	const apiDateParams = useMemo(() => ({
		start_date: dateRange?.startDate ? dayjs(dateRange.startDate).format('YYYY-MM-DD') : undefined,
		end_date: dateRange?.endDate ? dayjs(dateRange.endDate).format('YYYY-MM-DD') : undefined,
		// store_url: 'recode-studio.myshopify.com',
	}), [dateRange]);

	const queryOptions = useMemo(() => ({ 
		enabled: !!projectId && !!apiDateParams.start_date && !!apiDateParams.end_date 
	}), [projectId, apiDateParams.start_date, apiDateParams.end_date]);

	const {
		data: analyticsApiData,
		isLoading: isLoadingAnalytics,
		error: analyticsError,
	} = useAnalyticsData(projectId, apiDateParams, queryOptions);

	const { data: userFunnelApiData, isLoading: isLoadingUserFunnel, error: userFunnelError } = useUserFunnelData(projectId, apiDateParams, queryOptions);
	const { data: subscribersApiData, isLoading: isLoadingSubscribers, error: subscribersError } = useSubscribersData(projectId, apiDateParams, queryOptions);
	const { data: userByPlatformApiData, isLoading: isLoadingUserByPlatform, error: userByPlatformError } = useUserByPlatformData(projectId, apiDateParams, queryOptions);

	const generalStats = useMemo(() => {
		const liveAnalyticsStatsMap = {
			'Total Sales': analyticsApiData?.total_sales,
			'Average Order Value': analyticsApiData?.average_order_value,
			'Conversion Rate': analyticsApiData?.conversion_rate,
			'Order Count': analyticsApiData?.order_count,
			'In-App Revenue': analyticsApiData?.in_app_revenue,
		};

		return originalStatsData.map(stat => {
			// Priority 1: Active Users from userFunnelApiData
			if (stat.name === 'Active Users') {
				const activeUsersData = userFunnelApiData?.active_users;
				return {
					name: stat.name,
					stat: activeUsersData ? (activeUsersData.value?.toLocaleString() ?? '0') : 'N/A',
					change: activeUsersData ? (activeUsersData.percentage_change?.toFixed(0) ?? '0') : 'N/A',
					changeType: activeUsersData ? getChangeType(activeUsersData.percentage_change) : null,
					isLoading: isLoadingUserFunnel,
				};
			}

			// Priority 2: Subscriber data from subscribersApiData
			if (stat.name === 'Total Subscribers') {
				const totalSubscribersData = subscribersApiData?.total_subscribers;
				return {
					name: stat.name,
					stat: totalSubscribersData ? (totalSubscribersData.value?.toLocaleString() ?? '0') : 'N/A',
					change: totalSubscribersData ? (totalSubscribersData.percentage_change?.toFixed(0) ?? '0') : 'N/A',
					changeType: totalSubscribersData ? getChangeType(totalSubscribersData.percentage_change) : null,
					isLoading: isLoadingSubscribers,
				};
			}
			if (stat.name === 'Opt-in Rate') {
				const optInRateData = subscribersApiData?.opt_in_rate;
				return {
					name: stat.name,
					stat: optInRateData ? formatPercentage(optInRateData.value) : 'N/A',
					change: optInRateData ? (`${optInRateData.percentage_change?.toFixed(2) ?? '0.00'}%`) : 'N/A',
					changeType: optInRateData ? getChangeType(optInRateData.percentage_change) : null,
					isLoading: isLoadingSubscribers,
				};
			}

			// Priority 3: Stats from analyticsApiData
			const liveStatData = liveAnalyticsStatsMap[stat.name];
			if (liveStatData) {
				return {
					name: stat.name,
					stat: stat.name.includes('Value') || stat.name.includes('Sales') ? formatCurrency(liveStatData.value, analyticsApiData?.currency) : (stat.name.includes('Rate') || stat.name.includes('Revenue') ? formatPercentage(liveStatData.value) : liveStatData.value?.toLocaleString() ?? '0'),
					change: `${liveStatData.percentage_change?.toFixed(2) ?? '0.00'}%`,
					changeType: getChangeType(liveStatData.percentage_change),
					isLoading: isLoadingAnalytics,
				};
			}
			
			// Priority 4: Fallback to original static stat but show loading if any API is loading
			const isAnyApiLoading = isLoadingAnalytics || isLoadingUserFunnel || isLoadingSubscribers || isLoadingUserByPlatform;
			return { ...stat, isLoading: isAnyApiLoading }; 
		});
	}, [analyticsApiData, isLoadingAnalytics, userFunnelApiData, isLoadingUserFunnel, subscribersApiData, isLoadingSubscribers, isLoadingUserByPlatform]);

	const dynamicDataViews = useMemo(() => {
		// Check if any API is loading to show loading state for all charts initially
		const isAnyApiLoading = isLoadingAnalytics || isLoadingUserFunnel || isLoadingSubscribers || isLoadingUserByPlatform;

		return originalDataViewsConfig.map(viewConfig => {
			let headerRightWithProps = null;
			if (viewConfig.title === 'Total Sales') {
				headerRightWithProps = <ExportButtons projectId={projectId} apiDateParams={apiDateParams} />;
			}

			let chartDataToUse = [];
			let isLoadingState = false;

			switch (viewConfig.title) {
				case 'Total Sales':
					chartDataToUse = transformMultiSeriesDataForChart({ 
						totalsales: analyticsApiData?.total_sales, 
						refundscancellations: analyticsApiData?.total_cancellations 
					}, 'date');
					isLoadingState = isLoadingAnalytics;
					break;
				case 'Order Count':
					chartDataToUse = transformDataForChart(analyticsApiData?.order_count?.data_points);
					isLoadingState = isLoadingAnalytics;
					break;
				case 'App Launched':
					chartDataToUse = transformDataForChart(analyticsApiData?.total_app_launches?.data_points);
					isLoadingState = isLoadingAnalytics;
					break;
				case 'App Installs':
					chartDataToUse = transformDataForChart(analyticsApiData?.total_installs?.data_points);
					isLoadingState = isLoadingAnalytics;
					break;
				case 'User Funnel':
					const funnelAPIData = userFunnelApiData?.user_funnel;
					chartDataToUse = funnelAPIData
						? Object.entries(funnelAPIData).map(([key, val]) => ({
							date: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
							value: val || 0,
						  }))
						: [];
					isLoadingState = isLoadingUserFunnel;
					break;
				case 'Users by Platform':
					const platformData = userByPlatformApiData?.user_by_platform;
					chartDataToUse = platformData
						? [{
							date: 'Platforms',
							android: platformData.android ?? 0,
							ios: platformData.ios ?? 0,
						  }]
						: [];
					isLoadingState = isLoadingUserByPlatform;
					break;
				default:
					// For any charts not mapped to specific APIs, show loading if any API is loading
					chartDataToUse = [];
					isLoadingState = isAnyApiLoading;
			}
			return { 
				...viewConfig, 
				headerRight: headerRightWithProps,
				chartData: chartDataToUse, 
				isLoading: isLoadingState 
			};
		});
	}, [analyticsApiData, isLoadingAnalytics, userFunnelApiData, isLoadingUserFunnel, userByPlatformApiData, isLoadingUserByPlatform, subscribersApiData, isLoadingSubscribers, projectId, apiDateParams]);

	return (
		<div className="px-8 py-2 space-y-4">
			<dl className="grid grid-cols-2 gap-5 sm:grid-cols-4">
				{generalStats.map((item) => (
					<Stats key={item.name} item={item} isLoading={item.isLoading} />
				))}
			</dl>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				{dynamicDataViews.map((view, index) => (
					<DataView
						key={view.title} // Use title instead of index for better key stability
						title={view.title}
						className={view.className}
						chartMarkers={view.chartMarkers} 
						headerRight={view.headerRight}
						dateRange={dateRange}
						chartType={view.chartType}
						chartData={view.chartData}
						isLoading={view.isLoading}
					/>
				))}
			</div>
		</div>
	);
}
